import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle, CheckCircle2, Info, ShieldAlert, X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Replaces Flask's 85 `flash()` calls. Same idea, but the message now arrives
 * in an API response instead of a redirect, so it has to be raised explicitly.
 */

type ToastTone = "info" | "success" | "warning" | "danger";

type Toast = {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
};

type ToastInput = Omit<Toast, "id" | "tone">;

type ToastApi = {
  info: (t: ToastInput) => void;
  success: (t: ToastInput) => void;
  warning: (t: ToastInput) => void;
  danger: (t: ToastInput) => void;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const toneMeta: Record<ToastTone, { Icon: typeof Info; cls: string }> = {
  info: { Icon: Info, cls: "text-info-500" },
  success: { Icon: CheckCircle2, cls: "text-routine-500" },
  warning: { Icon: AlertTriangle, cls: "text-monitor-500" },
  danger: { Icon: ShieldAlert, cls: "text-critical-500" },
};

// Errors stay long enough to be read and acted on; confirmations get out of
// the way. Danger toasts never auto-dismiss.
const TTL: Record<ToastTone, number | null> = {
  info: 5000,
  success: 4000,
  warning: 8000,
  danger: null,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (tone: ToastTone, input: ToastInput) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { ...input, id, tone }]);
      const ttl = TTL[tone];
      if (ttl !== null) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), ttl),
        );
      }
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      info: (t) => push("info", t),
      success: (t) => push("success", t),
      warning: (t) => push("warning", t),
      danger: (t) => push("danger", t),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        // Polite: a toast should not cut off whatever the user is reading. The
        // emergency banners inside result screens use role="alert" instead.
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6"
      >
        {toasts.map((toast) => {
          const { Icon, cls } = toneMeta[toast.tone];
          return (
            <div
              key={toast.id}
              className={cn(
                "pointer-events-auto flex w-full max-w-sm gap-3 rounded-xl border px-4 py-3",
                "bg-surface border-hairline-strong shadow-float",
                "animate-in slide-in-from-bottom-2 fade-in duration-200",
              )}
            >
              <Icon aria-hidden className={cn("mt-0.5 size-4 shrink-0", cls)} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold">{toast.title}</p>
                {toast.description && (
                  <p className="text-content-muted mt-0.5 text-sm">{toast.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Tutup notifikasi"
                className="text-content-subtle hover:text-content -mr-1 h-fit shrink-0"
              >
                <X className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within <ToastProvider>");
  return context;
}
